import React, { useMemo } from 'react';
import { GroupBase, OptionProps } from 'react-select';
import { isTruthy, TRUNCATED_TEXT_TOOLTIP_DELAY_MS } from '@tanium/coreui-utils';
import { css } from '@tanium/react-emotion';
import { SimpleTooltip } from '@tanium/react-tooltip';
import { useAggregatedRef } from '@tanium/react-use-aggregated-ref';
import { useIsOverflowing } from '@tanium/react-use-is-overflowing';

export const SingleLineOption = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: OptionProps<Option, IsMulti, Group>,
) => {
  const {
    children,
    className,
    cx,
    getStyles,
    label,
    isDisabled,
    isFocused,
    isSelected,
    innerProps,
    innerRef,
  } = props;
  const [isOverflowing, overflowRef] = useIsOverflowing();

  const innerRefs = useAggregatedRef(
    useMemo(() => [innerRef, overflowRef], [innerRef, overflowRef]),
  );

  return (
    <SimpleTooltip
      delayShowMs={TRUNCATED_TEXT_TOOLTIP_DELAY_MS}
      placement="top"
      content={isOverflowing && label}
    >
      {({ ref }) => (
        <div ref={ref}>
          <div
            ref={innerRefs}
            className={cx(
              {
                option: true,
                'option--is-disabled': isDisabled,
                'option--is-focused': isFocused,
                'option--is-selected': isSelected,
              },
              [
                className,
                css(getStyles('option', props), {
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                }),
              ]
                .filter(isTruthy)
                .join(' '),
            )}
            {...innerProps}
          >
            {children}
          </div>
        </div>
      )}
    </SimpleTooltip>
  );
};
